import { useEffect, useState, type FC, type RefObject } from "react";
import { scrollToElement } from "../../lib/motion";

interface TocItem {
  id: string;
  title: string;
  level: 2 | 3;
}

interface TableOfContentsProps {
  /** Element that contains the rendered article body. */
  bodyRef: RefObject<HTMLElement | null>;
  /** Changes when the article changes, so headings are read again. */
  slug: string;
}

const TableOfContents: FC<TableOfContentsProps> = ({ bodyRef, slug }) => {
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  // The body is a lazy chunk, so watch for it to mount instead of reading once.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const read = () => {
      const next = Array.from(
        body.querySelectorAll<HTMLElement>("h2[id], h3[id]")
      ).map((el) => ({
        id: el.id,
        title: el.textContent ?? "",
        level: (el.tagName === "H2" ? 2 : 3) as 2 | 3,
      }));
      setItems((prev) =>
        JSON.stringify(prev) === JSON.stringify(next) ? prev : next
      );
    };

    read();
    const observer = new MutationObserver(read);
    observer.observe(body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [bodyRef, slug]);

  // Active section: the last heading that has scrolled past the header offset.
  // IntersectionObserver reads real window scroll, so it works with Lenis.
  useEffect(() => {
    if (items.length === 0 || typeof IntersectionObserver === "undefined") {
      return;
    }
    const headings = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null);
    const above = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Out of view above the band means the reader is past this heading.
          const past =
            !entry.isIntersecting && entry.boundingClientRect.top < 0;
          if (past || entry.isIntersecting) above.add(entry.target.id);
          else above.delete(entry.target.id);
        }
        const current = [...headings]
          .reverse()
          .find((el) => above.has(el.id));
        setActiveId(current?.id ?? null);
      },
      // Band starts below the fixed header (80px) and ends near the top third.
      { rootMargin: "-80px 0px -66% 0px" }
    );
    headings.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Table of contents"
      className="mb-8 rounded-xl border border-slate-700 bg-slate-900/60 p-4 lg:sticky lg:top-24 lg:mb-0 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto"
    >
      <p className="mb-2 text-sm font-bold uppercase tracking-wider text-slate-300">
        On this page
      </p>
      <ol className="space-y-1 text-sm">
        {items.map((item) => (
          <li key={item.id} className={item.level === 3 ? "pl-4" : undefined}>
            <a
              href={`#${item.id}`}
              aria-current={activeId === item.id ? "location" : undefined}
              className={`block rounded py-1 hover:text-cyan-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-400 ${
                activeId === item.id
                  ? "font-semibold text-cyan-300"
                  : "text-slate-300"
              }`}
              onClick={(e) => {
                e.preventDefault();
                scrollToElement(`#${CSS.escape(item.id)}`);
                history.replaceState(null, "", `#${item.id}`);
              }}
            >
              {item.title}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default TableOfContents;
